import React from 'react';
import { Trash2 } from 'lucide-react';
import { PencilFill, XCircleFill } from 'react-bootstrap-icons';
import {
  FormGroup,
  HeaderIconButton,
  Input,
  Label,
  ActionButton,
} from '../../../../styles/lesson/lesson.style';
import {
  useLessonCreationState,
  useLessonCreationDispatch,
  LessonItem,
} from '../lessonCreation.state';
import { LessonPlanHint } from '../../../register/InfoBlock';
import { formatInTimeZone } from 'date-fns-tz';
import { wallClockToISO, wallClockEndToISO } from '../../../../utils/timezones';

const formatDateTimeRangeShort = (
  date: string,
  startTime: string,
  endTime: string,
  tz: string,
) => {
  try {
    const startUtc = new Date(wallClockToISO(date, startTime, tz));
    const endUtc = new Date(wallClockEndToISO(date, startTime, endTime, tz));
    if (isNaN(startUtc.getTime()) || isNaN(endUtc.getTime())) {
      return `${date} ${startTime}–${endTime}`;
    }
    const datePart = formatInTimeZone(startUtc, tz, 'd MMM yyyy');
    const startTimePart = formatInTimeZone(startUtc, tz, 'HH:mm');
    const endTimePart = formatInTimeZone(endUtc, tz, 'HH:mm');
    return `${datePart}, ${startTimePart} – ${endTimePart}`;
  } catch {
    return `${date} ${startTime}–${endTime}`;
  }
};

// --- Component ---

export const Step5_LessonPlan: React.FC = () => {
  const { lessons, seriesName, isEditingSeriesName, editingLessonId, tutorTimezone, tutorName, existingEvents, tutorBusySlots, activeInputTz } =
    useLessonCreationState();
  const dispatch = useLessonCreationDispatch();

  const getConflictMessage = (date: string, time: string, endTime: string): string | null => {
    if (!date || !time || !endTime) return null;
    try {
      const newStart = wallClockToISO(date, time, activeInputTz);
      const newEnd = wallClockEndToISO(date, time, endTime, activeInputTz);

      const parentConflict = existingEvents.find(ev => ev.startAt < newEnd && ev.endAt > newStart);
      if (parentConflict) return `Conflicts with your booking "${parentConflict.title}"`;

      const tutorConflict = tutorBusySlots.find(s => s.startAt < newEnd && s.endAt > newStart);
      if (tutorConflict) return `${tutorName || 'Tutor'} already has a booking at this time`;

      return null;
    } catch { return null; }
  };

  // --- Handlers ---

  const handleToggleEditSeriesName = () => {
    dispatch({ type: 'SET_IS_EDITING_SERIES_NAME', payload: !isEditingSeriesName });
  };

  const handleSeriesNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SERIES_NAME', payload: e.target.value });
  };

  const handleToggleEditLesson = (id: string) => {
    const newEditingId = editingLessonId === id ? null : id;
    dispatch({ type: 'SET_EDITING_LESSON_ID', payload: newEditingId });
  };

  const handleUpdateLesson = (
    id: string,
    patch: Partial<LessonItem>,
  ) => {
    dispatch({ type: 'UPDATE_LESSON', payload: { id, patch } });
  };

  const handleDeleteLesson = (id: string) => {
    dispatch({ type: 'DELETE_LESSON', payload: id });
  };

  // --- Render ---
  return (
    <>
      <LessonPlanHint />
      <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: '0.5rem 0 0' }}>
        All times shown in your timezone ({activeInputTz.split('/').pop()?.replace(/_/g, ' ')})
      </p>

      <FormGroup css={{ paddingTop: 0, borderTop: 'none', marginTop: '1rem' }}>
        {lessons.length === 0 ? (
          <div style={{ color: '#6b7280' }}>No lessons created.</div>
        ) : (
          <>
            {/* --- Series Name Editor --- */}
            {lessons.length > 1 && (
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '0.75rem 0.75rem',
                  marginBottom: '1.5rem',
                  backgroundColor: isEditingSeriesName
                    ? 'var(--colors-blue3)'
                    : 'var(--colors-blue3)',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}
                >
                  <div style={{ fontSize: '$md' }}>
                    <b>Series Title:</b> {seriesName}
                  </div>
                  <HeaderIconButton
                    aria-label={
                      isEditingSeriesName ? 'Save title' : 'Edit series title'
                    }
                    onClick={handleToggleEditSeriesName}
                    data-active={isEditingSeriesName}
                    title={isEditingSeriesName ? 'Save' : 'Edit'}
                  >
                    {isEditingSeriesName ? (
                      <XCircleFill size={18} />
                    ) : (
                      <PencilFill size={18} />
                    )}
                  </HeaderIconButton>
                </div>

                {isEditingSeriesName && (
                  <div style={{ marginTop: '1rem' }}>
                    <Input
                      id="seriesName"
                      type="text"
                      value={seriesName}
                      onChange={handleSeriesNameChange}
                      placeholder="e.g., A-Level Maths Revision"
                    />
                  </div>
                )}
              </div>
            )}

            {/* --- Lessons List --- */}
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {lessons.map((l) => {
                const isEditing = editingLessonId === l.id;
                const conflictMsg = getConflictMessage(l.date, l.time, l.endTime);

                return (
                  <div
                    key={l.id}
                    style={{
                      border: conflictMsg ? '1px solid #fca5a5' : '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: '0.75rem 0.75rem',
                      transition: 'background-color 0.2s',
                      backgroundColor: isEditing ? '#f9fafb' : conflictMsg ? '#fef2f2' : 'white',
                    }}
                  >
                    {/* --- Row Header (Non-edit view) --- */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        alignItems: 'center',
                        gap: '0.75rem',
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>
                        {l.title} — {formatDateTimeRangeShort(l.date, l.time, l.endTime, activeInputTz)}
                      </div>
                      <HeaderIconButton
                        aria-label={isEditing ? 'Save changes' : 'Edit lesson'}
                        onClick={() => handleToggleEditLesson(l.id)}
                        data-active={isEditing}
                        title={isEditing ? 'Save' : 'Edit'}
                      >
                        {isEditing ? (
                          <XCircleFill size={18} />
                        ) : (
                          <PencilFill size={18} />
                        )}
                      </HeaderIconButton>
                    </div>

                    {conflictMsg && (
                      <p style={{ color: '#dc2626', fontSize: '0.75rem', margin: '0.35rem 0 0' }}>
                        {conflictMsg} — change the time to proceed.
                      </p>
                    )}

                    {/* --- In-line Editor --- */}
                    {isEditing && (
                      <div
                        style={{
                          marginTop: '1rem',
                          display: 'grid',
                          gap: '1rem',
                        }}
                      >
                        {/* Editor Row 1: Title & Delete */}
                        <div>
                          <Label
                            htmlFor={`title-${l.id}`}
                            css={{ fontSize: '$sm', marginBottom: '0.25rem' }}
                          >
                            Title
                          </Label>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: '0.75rem',
                            }}
                          >
                            <Input
                              type="text"
                              id={`title-${l.id}`}
                              value={l.title}
                              onChange={(e) =>
                                handleUpdateLesson(l.id, {
                                  title: e.target.value,
                                  customTitle: true,
                                })
                              }
                            />
                            <ActionButton
                              intent="background"
                              aria-label="Delete lesson"
                              onClick={() => handleDeleteLesson(l.id)}
                              title="Delete"
                            >
                              <Trash2 size={18} stroke="red" />
                            </ActionButton>
                          </div>
                        </div>

                        {/* Editor Row 2: Date & Times */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1.5fr 1fr 1fr',
                            gap: '0.75rem',
                            alignItems: 'flex-end',
                          }}
                        >
                          <div>
                            <Label
                              htmlFor={`date-${l.id}`}
                              css={{ fontSize: '$sm', marginBottom: '0.25rem' }}
                            >
                              Date
                            </Label>
                            <Input
                              type="date"
                              id={`date-${l.id}`}
                              value={l.date}
                              onChange={(e) =>
                                handleUpdateLesson(l.id, { date: e.target.value })
                              }
                            />
                          </div>
                          <div>
                            <Label
                              htmlFor={`start-time-${l.id}`}
                              css={{ fontSize: '$sm', marginBottom: '0.25rem' }}
                            >
                              Start
                            </Label>
                            <Input
                              type="time"
                              id={`start-time-${l.id}`}
                              value={l.time}
                              onChange={(e) =>
                                handleUpdateLesson(l.id, { time: e.target.value })
                              }
                            />
                          </div>
                          <div>
                            <Label
                              htmlFor={`end-time-${l.id}`}
                              css={{ fontSize: '$sm', marginBottom: '0.25rem' }}
                            >
                              End
                            </Label>
                            <Input
                              type="time"
                              id={`end-time-${l.id}`}
                              value={l.endTime}
                              onChange={(e) =>
                                handleUpdateLesson(l.id, { endTime: e.target.value })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </FormGroup>
    </>
  );
};